import { createReducer, on } from '@ngrx/store';
import * as SquareDeviceActions from './square-device.actions';
import { SquareDevice } from './square-device.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface SquareDevicesState {
    squareDevices: SquareDevice[];
    squareDevice: SquareDevice | null;
    unassignedSquareDevices: SquareDevice[];
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialSquareDevicesState: SquareDevicesState = {
    squareDevices: [],
    squareDevice: null,
    unassignedSquareDevices: [],
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

export const squareDevicesReducer = createReducer(
    initialSquareDevicesState,

    // On Load All
    on(SquareDeviceActions.loadSquareDevices, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SquareDeviceActions.loadSquareDevicesSuccess, (state, { squareDevices }) => ({
        ...state,
        isLoading: false,
        squareDevices,
    })),
    on(SquareDeviceActions.loadSquareDevicesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // On Load Single
    on(SquareDeviceActions.loadSquareDevice, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SquareDeviceActions.loadSquareDeviceSuccess, (state, { squareDevice }) => ({
        ...state,
        isLoading: false,
        squareDevice,
    })),
    on(SquareDeviceActions.loadSquareDeviceFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // On Create
    on(SquareDeviceActions.createSquareDevice, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SquareDeviceActions.createSquareDeviceSuccess, (state, { squareDevice }) => ({
        ...state,
        isLoading: false,
        squareDevices: [...(state.squareDevices || []), squareDevice],
    })),
    on(SquareDeviceActions.createSquareDeviceFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // On Update
    on(SquareDeviceActions.updateSquareDevice, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SquareDeviceActions.updateSquareDeviceSuccess, (state, { squareDevice }) => ({
        ...state,
        isLoading: false,
        squareDevices: state.squareDevices.map((s) =>
            s.id === squareDevice.id ? squareDevice : s
        ),
    })),
    on(SquareDeviceActions.updateSquareDeviceFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // On Delete
    on(SquareDeviceActions.deleteSquareDevice, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SquareDeviceActions.deleteSquareDeviceSuccess, (state, { id }) => ({
        ...state,
        isLoading: false,
        squareDevices: state.squareDevices.filter((s) => s.id !== id),
    })),
    on(SquareDeviceActions.deleteSquareDeviceFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // On Load Unassigned
    on(SquareDeviceActions.loadUnassignedSquareDevices, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SquareDeviceActions.loadUnassignedSquareDevicesSuccess, (state, { unassignedSquareDevices }) => ({
        ...state,
        isLoading: false,
        unassignedSquareDevices,
    })),
    on(SquareDeviceActions.loadUnassignedSquareDevicesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    }))
);
